import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { usersApi, leavePoliciesApi } from '../../services/api';
import useStore from '../../store';
import './AdminUsers.css';

export default function AdminUsersPage() {
  const navigate = useNavigate();
  const { user: currentUser } = useStore();
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({});
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [policyFilter, setPolicyFilter] = useState('');
  const [departments, setDepartments] = useState([]);
  const [leavePolicies, setLeavePolicies] = useState([]);
  const [regions, setRegions] = useState([]);
  const [countries, setCountries] = useState([]);
  const [managers, setManagers] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleteConfirmText, setBulkDeleteConfirmText] = useState('');
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkUpdateOpen, setBulkUpdateOpen] = useState(false);
  const [bulkUpdateManagerId, setBulkUpdateManagerId] = useState('');
  const [bulkUpdateCountryId, setBulkUpdateCountryId] = useState('');
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [newMember, setNewMember] = useState({
    email: '',
    first_name: '',
    last_name: '',
    role: 'employee',
    employee_type: 'full_time',
    designation: '',
    company: '',
    department_id: '',
    region_id: '',
    manager_id: '',
    joining_date: '',
    date_of_birth: '',
    mobile_number: '',
    address: '',
    temporary_address: '',
    emergency_contact_name: '',
    emergency_contact_number: '',
    emergency_contact_relation: '',
    country_code: 'IN',
    is_active: true,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, deptRes, leavePoliciesRes, regionsRes, countriesRes, managersRes] = await Promise.all([
        usersApi.list({
          search: search || undefined,
          role: roleFilter || undefined,
          department_id: deptFilter || undefined,
          company: companyFilter || undefined,
          leave_policy_id: policyFilter || undefined,
          page,
          limit: 200,
        }),
        usersApi.departments(),
        leavePoliciesApi.list(),
        usersApi.regions(),
        usersApi.countries(),
        usersApi.list({ role: 'manager', page: 1, limit: 200 }),
      ]);
      setUsers(usersRes.data.data);
      setPagination(usersRes.data.pagination);
      setDepartments(deptRes.data.data);
      setLeavePolicies(leavePoliciesRes.data.data || []);
      setRegions(regionsRes.data.data || []);
      setCountries(countriesRes.data.data || []);
      setManagers(managersRes.data.data || []);
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [search, roleFilter, deptFilter, companyFilter, policyFilter, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSelectedIds([]); }, [users]);

  const roleBadge = (role) => {
    const map = { admin: 'badge-rose', manager: 'badge-amber', employee: 'badge-violet' };
    const label = role ? role.charAt(0).toUpperCase() + role.slice(1) : role;
    return <span className={`badge ${map[role] || 'badge-gray'}`} style={{ fontSize: '0.6875rem' }}>{label}</span>;
  };

  const typeBadge = (type) => (
    <span className={`badge ${type === 'contractor' ? 'badge-amber' : 'badge-sky'}`} style={{ fontSize: '0.6875rem' }}>
      {type === 'contractor' ? 'Contractor' : 'Full Time'}
    </span>
  );

  const visibleUsers = policyFilter
    ? users.filter((u) => String(u.leave_policy_id || u.leavePolicy?.id || '') === String(policyFilter))
    : users;

  const SORT_ACCESSORS = {
    name: (u) => `${u.first_name || ''} ${u.last_name || ''}`.trim().toLowerCase(),
    policy: (u) => u.leavePolicy?.name || '',
    manager: (u) => (u.manager ? `${u.manager.first_name} ${u.manager.last_name}` : ''),
    joined: (u) => u.joining_date || '',
    country: (u) => u.country?.name || '',
  };

  const sortedUsers = sortColumn
    ? [...visibleUsers].sort((a, b) => {
        const aVal = SORT_ACCESSORS[sortColumn](a);
        const bVal = SORT_ACCESSORS[sortColumn](b);
        const cmp = typeof aVal === 'number' ? aVal - bVal : String(aVal).localeCompare(String(bVal));
        return sortDirection === 'asc' ? cmp : -cmp;
      })
    : visibleUsers;

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const sortArrow = (column) => (sortColumn === column ? (sortDirection === 'asc' ? ' ↑' : ' ↓') : '');

  const resetNewMember = () => {
    setNewMember({
      email: '',
      first_name: '',
      last_name: '',
      role: 'employee',
      employee_type: 'full_time',
      designation: '',
      company: '',
      department_id: '',
      region_id: '',
      manager_id: '',
      joining_date: '',
      date_of_birth: '',
      mobile_number: '',
      address: '',
      temporary_address: '',
      emergency_contact_name: '',
      emergency_contact_number: '',
      emergency_contact_relation: '',
      country_code: 'IN',
      is_active: true,
    });
  };

  const handleCreateMember = async (e) => {
    e.preventDefault();
    if (!newMember.email.trim() || !newMember.first_name.trim() || !newMember.last_name.trim()) {
      toast.error('Email, first name and last name are required');
      return;
    }

    setCreating(true);
    try {
      await usersApi.create({
        email: newMember.email.trim().toLowerCase(),
        first_name: newMember.first_name.trim(),
        last_name: newMember.last_name.trim(),
        role: newMember.role,
        employee_type: newMember.employee_type,
        designation: newMember.designation || null,
        company: newMember.company || null,
        department_id: newMember.department_id || null,
        region_id: newMember.region_id || null,
        manager_id: newMember.manager_id || null,
        joining_date: newMember.joining_date || null,
        date_of_birth: newMember.date_of_birth || null,
        mobile_number: newMember.mobile_number || null,
        address: newMember.address || null,
        temporary_address: newMember.temporary_address || null,
        emergency_contact_name: newMember.emergency_contact_name || null,
        emergency_contact_number: newMember.emergency_contact_number || null,
        emergency_contact_relation: newMember.emergency_contact_relation || null,
        is_active: newMember.is_active,
      });

      toast.success('Member added successfully');
      setShowAddModal(false);
      resetNewMember();
      setPage(1);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add member');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteTarget || deleteConfirmText.trim().toLowerCase() !== deleteTarget.email.toLowerCase()) return;
    setDeleting(true);
    try {
      const res = await usersApi.delete(deleteTarget.id);
      const d = res.data.deleted || {};
      toast.success(
        `${deleteTarget.first_name} ${deleteTarget.last_name} deleted — ` +
        `${d.attendanceRecords || 0} attendance, ${d.leaveBalances || 0} leave balances, ` +
        `${d.leaveRequests || 0} leave requests removed.`
      );
      setDeleteTarget(null);
      setDeleteConfirmText('');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleSelectAllVisible = (selectableIds) => {
    const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.includes(id));
    setSelectedIds(allSelected ? [] : selectableIds);
  };

  const handleBulkDelete = async () => {
    if (bulkDeleteConfirmText.trim().toUpperCase() !== 'DELETE' || selectedIds.length === 0) return;
    setBulkDeleting(true);
    try {
      const res = await usersApi.bulkDelete(selectedIds);
      const d = res.data.deleted || {};
      toast.success(
        `${d.users || 0} user(s) deleted — ` +
        `${d.attendanceRecords || 0} attendance, ${d.leaveBalances || 0} leave balances, ` +
        `${d.leaveRequests || 0} leave requests removed.`
      );
      setSelectedIds([]);
      setBulkDeleteOpen(false);
      setBulkDeleteConfirmText('');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete selected users');
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleBulkUpdate = async () => {
    if (!bulkUpdateManagerId && !bulkUpdateCountryId) {
      toast.error('Pick a manager and/or country to apply.');
      return;
    }
    setBulkUpdating(true);
    try {
      const payload = { ids: selectedIds };
      if (bulkUpdateManagerId) payload.manager_id = bulkUpdateManagerId;
      if (bulkUpdateCountryId) payload.country_id = bulkUpdateCountryId;
      const res = await usersApi.bulkUpdate(payload);
      toast.success(`${res.data.updated || 0} user(s) updated.`);
      setSelectedIds([]);
      setBulkUpdateOpen(false);
      setBulkUpdateManagerId('');
      setBulkUpdateCountryId('');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update selected users');
    } finally {
      setBulkUpdating(false);
    }
  };

  return (
    <div className="admin-users-page">
      <div className="page-header">
        <div>
          <div className="page-title">User Management</div>
          <div className="page-subtitle">Manage all employees, roles and assignments</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {selectedIds.length > 0 && (
            <>
              <button className="btn btn-secondary" onClick={() => { setBulkUpdateManagerId(''); setBulkUpdateCountryId(''); setBulkUpdateOpen(true); }}>
                Update Selected ({selectedIds.length})
              </button>
              <button className="btn btn-danger" onClick={() => { setBulkDeleteConfirmText(''); setBulkDeleteOpen(true); }}>
                Delete Selected ({selectedIds.length})
              </button>
            </>
          )}
          <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
            + Add Member
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card admin-filters animate-in">
        <div className="admin-filters-inner">
          <div className="filter-search">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--text-muted)', flexShrink: 0 }}>
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              className="filter-search-input"
              placeholder="Search by name or email…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            />
          </div>
          <select className="form-select" style={{ width: 160 }} value={roleFilter} onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}>
            <option value="">All Roles</option>
            <option value="admin">Admin</option>
            <option value="manager">Manager</option>
            <option value="employee">Employee</option>
          </select>
          <select className="form-select" style={{ width: 200 }} value={deptFilter} onChange={(e) => { setDeptFilter(e.target.value); setPage(1); }}>
            <option value="">All Departments</option>
            {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          <select
            className="form-select"
            style={{ width: 200 }}
            value={companyFilter}
            onChange={(e) => { setCompanyFilter(e.target.value); setPage(1); }}
          >
            <option value="">All Companies</option>
            <option value="Aays">Aays</option>
            <option value="Aidetic">Aidetic</option>
          </select>
          <select
            className="form-select"
            style={{ width: 220 }}
            value={policyFilter}
            onChange={(e) => { setPolicyFilter(e.target.value); setPage(1); }}
          >
            <option value="">All Policies</option>
            {leavePolicies.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <button className="btn btn-secondary btn-sm" onClick={() => { setSearch(''); setRoleFilter(''); setDeptFilter(''); setCompanyFilter(''); setPolicyFilter(''); setPage(1); }}>
            Reset
          </button>
        </div>
        {pagination.total !== undefined && (
          <span className="filter-count">{pagination.total} employees</span>
        )}
      </div>

      {/* Table */}
      <div className="card animate-in stagger-1" style={{ padding: 0 }}>
        {loading ? (
          <div style={{ padding: 40 }}>
            {[...Array(5)].map((_, i) => (
              <div key={i} className="skeleton" style={{ height: 48, marginBottom: 8, borderRadius: 8 }} />
            ))}
          </div>
        ) : visibleUsers.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">👥</div>
            <div className="empty-state-title">No users found</div>
          </div>
        ) : (
          <div className="table-wrapper" style={{ border: 'none', borderRadius: 'var(--radius-lg)' }}>
            <table>
              <thead>
                <tr>
                  <th style={{ width: 36 }}>
                    <input
                      type="checkbox"
                      checked={(() => {
                        const selectableIds = visibleUsers.filter((u) => u.id !== currentUser?.id).map((u) => u.id);
                        return selectableIds.length > 0 && selectableIds.every((id) => selectedIds.includes(id));
                      })()}
                      onChange={() => toggleSelectAllVisible(visibleUsers.filter((u) => u.id !== currentUser?.id).map((u) => u.id))}
                    />
                  </th>
                  <th>
                    <span className="sortable" style={{ cursor: 'pointer' }} onClick={() => handleSort('name')}>Employee{sortArrow('name')}</span>
                  </th>
                  <th>
                    <span className="sortable" style={{ cursor: 'pointer' }} onClick={() => handleSort('manager')}>Manager{sortArrow('manager')}</span>
                  </th>
                  <th>
                    <span className="sortable" style={{ cursor: 'pointer' }} onClick={() => handleSort('country')}>Country{sortArrow('country')}</span>
                  </th>
                  <th>
                    <span className="sortable" style={{ cursor: 'pointer' }} onClick={() => handleSort('joined')}>Joined{sortArrow('joined')}</span>
                  </th>
                  <th>
                    <span className="sortable" style={{ cursor: 'pointer' }} onClick={() => handleSort('policy')}>Policy{sortArrow('policy')}</span>
                  </th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {sortedUsers.map((u) => {
                  const initials = `${u.first_name?.[0] || ''}${u.last_name?.[0] || ''}`.toUpperCase();
                  return (
                    <tr key={u.id}>
                      <td>
                        {u.id !== currentUser?.id && (
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(u.id)}
                            onChange={() => toggleSelect(u.id)}
                          />
                        )}
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ position: 'relative', flexShrink: 0 }}>
                            <div className="avatar avatar-sm" style={{ background: u.role === 'admin' ? '#EF4444' : u.role === 'manager' ? '#F59E0B' : 'var(--brand-violet)' }}>
                              {initials}
                            </div>
                            <span
                              title={u.is_active ? 'Active' : 'Inactive'}
                              style={{
                                position: 'absolute',
                                bottom: -1,
                                right: -1,
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                background: u.is_active ? 'var(--accent-emerald)' : 'var(--accent-rose)',
                                border: '2px solid var(--surface-0)',
                              }}
                            />
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{u.first_name} {u.last_name}</div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{u.email}</div>
                            <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                              {roleBadge(u.role)}
                              {typeBadge(u.employee_type)}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                        {u.manager ? `${u.manager.first_name} ${u.manager.last_name}` : '—'}
                      </td>
                      <td style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                        {u.country?.name || '—'}
                      </td>
                      <td style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                        {u.joining_date ? new Date(u.joining_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                      </td>
                      <td>
                        {u.leavePolicy?.name ? (
                          <span className="badge badge-violet" style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                            📋 {u.leavePolicy.name}
                          </span>
                        ) : (
                          <span className="badge badge-gray" style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                            — Unassigned
                          </span>
                        )}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => navigate(`/admin/users/${u.id}`)}
                          >
                            Edit
                          </button>
                          {u.id !== currentUser?.id && (
                            <button
                              className="btn btn-sm"
                              style={{ background: '#FEE2E2', color: '#991B1B' }}
                              onClick={() => { setDeleteTarget(u); setDeleteConfirmText(''); }}
                              title="Permanently delete this user and all their records"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="pagination">
            <button className="btn btn-secondary btn-sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹ Prev</button>
            <span className="pagination-info">Page {page} of {pagination.totalPages}</span>
            <button className="btn btn-secondary btn-sm" disabled={page === pagination.totalPages} onClick={() => setPage(p => p + 1)}>Next ›</button>
          </div>
        )}
      </div>

      {showAddModal && (
        <div className="modal-overlay" onClick={() => !creating && setShowAddModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <div className="page-title" style={{ fontSize: '1.125rem' }}>Add Member</div>
                <div className="page-subtitle">Create a new employee profile</div>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowAddModal(false)} disabled={creating}>✕</button>
            </div>

            <form onSubmit={handleCreateMember}>
              <div className="modal-body">
                <div className="admin-create-grid">
                  <div className="form-group">
                    <label className="form-label">Email *</label>
                    <input
                      className="form-input"
                      type="email"
                      required
                      value={newMember.email}
                      onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Role</label>
                    <select
                      className="form-select"
                      value={newMember.role}
                      onChange={(e) => setNewMember({ ...newMember, role: e.target.value })}
                    >
                      <option value="admin">Admin</option>
                      <option value="manager">Manager</option>
                      <option value="employee">Employee</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">First Name *</label>
                    <input
                      className="form-input"
                      required
                      value={newMember.first_name}
                      onChange={(e) => setNewMember({ ...newMember, first_name: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Last Name *</label>
                    <input
                      className="form-input"
                      required
                      value={newMember.last_name}
                      onChange={(e) => setNewMember({ ...newMember, last_name: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Employee Type</label>
                    <select
                      className="form-select"
                      value={newMember.employee_type}
                      onChange={(e) => setNewMember({ ...newMember, employee_type: e.target.value })}
                    >
                      <option value="full_time">Full Time</option>
                      <option value="contractor">Contractor</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Joining Date</label>
                    <input
                      type="date"
                      className="form-input"
                      value={newMember.joining_date}
                      onChange={(e) => setNewMember({ ...newMember, joining_date: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Date of Birth</label>
                    <input
                      type="date"
                      className="form-input"
                      value={newMember.date_of_birth}
                      onChange={(e) => setNewMember({ ...newMember, date_of_birth: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Designation</label>
                    <input
                      className="form-input"
                      value={newMember.designation}
                      onChange={(e) => setNewMember({ ...newMember, designation: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Company</label>
                    <select
                      className="form-select"
                      value={newMember.company}
                      onChange={(e) => setNewMember({ ...newMember, company: e.target.value })}
                    >
                      <option value="">- Select -</option>
                      <option value="Aays">Aays</option>
                      <option value="Aidetic">Aidetic</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Mobile Number</label>
                    <input
                      className="form-input"
                      value={newMember.mobile_number}
                      onChange={(e) => setNewMember({ ...newMember, mobile_number: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Department</label>
                    <select
                      className="form-select"
                      value={newMember.department_id}
                      onChange={(e) => setNewMember({ ...newMember, department_id: e.target.value })}
                    >
                      <option value="">- Select -</option>
                      {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Country</label>
                    <select
                      className="form-select"
                      value={newMember.country_code}
                      onChange={(e) => setNewMember({ ...newMember, country_code: e.target.value })}
                    >
                      <option value="">- Select -</option>
                      {countries.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Region</label>
                    <select
                      className="form-select"
                      value={newMember.region_id}
                      onChange={(e) => setNewMember({ ...newMember, region_id: e.target.value })}
                    >
                      <option value="">- Select -</option>
                      {regions.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                  </div>

                  <div className="form-group admin-create-full-row">
                    <label className="form-label">Manager</label>
                    <select
                      className="form-select"
                      value={newMember.manager_id}
                      onChange={(e) => setNewMember({ ...newMember, manager_id: e.target.value })}
                    >
                      <option value="">- Select -</option>
                      {managers.map((m) => (
                        <option key={m.id} value={m.id}>{m.first_name} {m.last_name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group admin-create-full-row">
                    <label className="form-label">Address</label>
                    <textarea
                      className="form-textarea"
                      rows={2}
                      value={newMember.address}
                      onChange={(e) => setNewMember({ ...newMember, address: e.target.value })}
                    />
                  </div>

                  <div className="form-group admin-create-full-row">
                    <label className="form-label">Temporary Address</label>
                    <textarea
                      className="form-textarea"
                      rows={2}
                      value={newMember.temporary_address}
                      onChange={(e) => setNewMember({ ...newMember, temporary_address: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Emergency Contact Name</label>
                    <input
                      className="form-input"
                      value={newMember.emergency_contact_name}
                      onChange={(e) => setNewMember({ ...newMember, emergency_contact_name: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Emergency Contact Number</label>
                    <input
                      className="form-input"
                      value={newMember.emergency_contact_number}
                      onChange={(e) => setNewMember({ ...newMember, emergency_contact_number: e.target.value })}
                    />
                  </div>

                  <div className="form-group admin-create-full-row">
                    <label className="form-label">Emergency Contact Relation</label>
                    <input
                      className="form-input"
                      value={newMember.emergency_contact_relation}
                      onChange={(e) => setNewMember({ ...newMember, emergency_contact_relation: e.target.value })}
                    />
                  </div>

                  <label className="admin-create-checkbox">
                    <input
                      type="checkbox"
                      checked={newMember.is_active}
                      onChange={(e) => setNewMember({ ...newMember, is_active: e.target.checked })}
                    />
                    Active user
                  </label>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowAddModal(false)} disabled={creating}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={creating}>
                  {creating ? 'Adding...' : 'Add Member'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete user confirmation — destructive, cascades to all of their records */}
      {deleteTarget && (
        <div className="modal-overlay" onClick={() => !deleting && setDeleteTarget(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ color: 'var(--accent-rose)' }}>Delete {deleteTarget.first_name} {deleteTarget.last_name}?</h3>
              <button className="btn btn-ghost btn-sm" onClick={() => setDeleteTarget(null)} disabled={deleting}>✕</button>
            </div>
            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div className="error-alert" style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', padding: '12px 16px', borderRadius: 10, fontSize: '0.875rem' }}>
                This permanently deletes <strong>{deleteTarget.email}</strong> and all of their data — attendance records,
                leave balances, leave requests, and synced master data. This cannot be undone.
              </div>
              <div className="form-group">
                <label className="form-label">
                  Type <strong>{deleteTarget.email}</strong> to confirm
                </label>
                <input
                  className="form-input"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder={deleteTarget.email}
                  autoFocus
                />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</button>
              <button
                type="button"
                className="btn btn-danger"
                disabled={deleting || deleteConfirmText.trim().toLowerCase() !== deleteTarget.email.toLowerCase()}
                onClick={handleDeleteUser}
              >
                {deleting ? 'Deleting…' : 'Permanently Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk delete confirmation — destructive, cascades to all selected users' records */}
      {bulkDeleteOpen && (
        <div className="modal-overlay" onClick={() => !bulkDeleting && setBulkDeleteOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ color: 'var(--accent-rose)' }}>Delete {selectedIds.length} user(s)?</h3>
              <button className="btn btn-ghost btn-sm" onClick={() => setBulkDeleteOpen(false)} disabled={bulkDeleting}>✕</button>
            </div>
            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div className="error-alert" style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', padding: '12px 16px', borderRadius: 10, fontSize: '0.875rem' }}>
                This permanently deletes all {selectedIds.length} selected users and their data — attendance records,
                leave balances, leave requests, and synced master data. This cannot be undone.
              </div>
              <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 10, padding: '8px 12px' }}>
                {users.filter((u) => selectedIds.includes(u.id)).map((u) => (
                  <div key={u.id} style={{ fontSize: '0.8125rem', padding: '4px 0' }}>
                    <strong>{u.first_name} {u.last_name}</strong> <span style={{ color: 'var(--text-muted)' }}>({u.email})</span>
                  </div>
                ))}
              </div>
              <div className="form-group">
                <label className="form-label">
                  Type <strong>DELETE</strong> to confirm
                </label>
                <input
                  className="form-input"
                  value={bulkDeleteConfirmText}
                  onChange={(e) => setBulkDeleteConfirmText(e.target.value)}
                  placeholder="DELETE"
                  autoFocus
                />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setBulkDeleteOpen(false)} disabled={bulkDeleting}>Cancel</button>
              <button
                type="button"
                className="btn btn-danger"
                disabled={bulkDeleting || bulkDeleteConfirmText.trim().toUpperCase() !== 'DELETE'}
                onClick={handleBulkDelete}
              >
                {bulkDeleting ? 'Deleting…' : `Permanently Delete ${selectedIds.length}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk update — assign manager and/or country to all selected users */}
      {bulkUpdateOpen && (
        <div className="modal-overlay" onClick={() => !bulkUpdating && setBulkUpdateOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Update {selectedIds.length} user(s)</h3>
              <button className="btn btn-ghost btn-sm" onClick={() => setBulkUpdateOpen(false)} disabled={bulkUpdating}>✕</button>
            </div>
            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
                Only the fields you pick below will be applied — leave a field as "No change" to keep each user's existing value.
              </div>
              <div style={{ maxHeight: 140, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 10, padding: '8px 12px' }}>
                {users.filter((u) => selectedIds.includes(u.id)).map((u) => (
                  <div key={u.id} style={{ fontSize: '0.8125rem', padding: '4px 0' }}>
                    <strong>{u.first_name} {u.last_name}</strong> <span style={{ color: 'var(--text-muted)' }}>({u.email})</span>
                  </div>
                ))}
              </div>
              <div className="form-group">
                <label className="form-label">Manager</label>
                <select
                  className="form-select"
                  value={bulkUpdateManagerId}
                  onChange={(e) => setBulkUpdateManagerId(e.target.value)}
                >
                  <option value="">— No change —</option>
                  {managers.map((m) => (
                    <option key={m.id} value={m.id}>{m.first_name} {m.last_name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Country</label>
                <select
                  className="form-select"
                  value={bulkUpdateCountryId}
                  onChange={(e) => setBulkUpdateCountryId(e.target.value)}
                >
                  <option value="">— No change —</option>
                  {countries.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setBulkUpdateOpen(false)} disabled={bulkUpdating}>Cancel</button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={bulkUpdating || (!bulkUpdateManagerId && !bulkUpdateCountryId)}
                onClick={handleBulkUpdate}
              >
                {bulkUpdating ? 'Updating…' : `Apply to ${selectedIds.length}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
